import {
  PRESET_TITLE_MAX_CHARACTERS,
  VERSION_NUMBER_LENGTH,
  VersionNumber,
} from "@pax/api-contract/presets/v1alpha1";
import {
  focusHaloFlush,
  HapticSurface,
  panelMotion,
  press,
  solidSurface,
  usePanelDismiss,
} from "@pax/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { X } from "lucide-react";
import { type AnimationEvent, useState } from "react";
import { sessionQuery } from "@/features/auth/queries";
import { useCharacterLimit, versionNumberProblem } from "@/features/preset-editor/NewVersionDialog";
import { myPresetsQuery, refreshPresets } from "@/features/preset-editor/queries";
import { api, requestErrorMessage } from "@/lib/api";
import { ScreenOverlay } from "@/shell/Screen";

/** A slug from a title: its words in lowercase, joined by hyphens. */
function slugOf(title: string): string {
  return title
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 64)
    .replace(/-+$/, "");
}

const SLUG = /^[a-z0-9][a-z0-9-]{1,62}[a-z0-9]$/;

/**
 * Where writing a scenario begins: a new one of your own, the ones you work on, and the
 * invitations waiting for you.
 */
export function WriteScenarioPanel({
  leaving,
  onLeft,
  onClose,
}: {
  /** True from the moment it is asked to go until it has gone. */
  leaving: boolean;
  onLeft: (event: AnimationEvent<HTMLElement>) => void;
  onClose: () => void;
}) {
  // The scrim covers everything, so "outside the panel" and "on the scrim" are
  // the same place; routing presses through the trigger as well would only
  // have the trigger reopening what it closed.
  const panel = usePanelDismiss<HTMLDivElement>({
    open: !leaving,
    onDismiss: onClose,
    dismissOnOutsidePress: false,
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: session } = useQuery(sessionQuery);
  const account = Boolean(session?.user && !session.user.isAnonymous);
  const { data: mine } = useQuery({ ...myPresetsQuery(), enabled: account });
  const [title, setTitle] = useState("");
  const [slug, setSlug] = useState<string | null>(null);
  const [versionNumber, setVersionNumber] = useState("");
  /** Whether the form was sent since the number last changed, so unfinished numbers say why. */
  const [submitted, setSubmitted] = useState(false);
  const numberLimit = useCharacterLimit(VERSION_NUMBER_LENGTH);
  const numberProblem = numberLimit.tooLong ?? versionNumberProblem(versionNumber, submitted);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const chosenSlug = slug ?? slugOf(title);

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setProblem(null);
    try {
      await work();
    } catch (error) {
      setProblem(requestErrorMessage(error));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ScreenOverlay>
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        onClick={onClose}
        data-leaving={leaving || undefined}
        className="overlay-scrim absolute inset-0 cursor-default"
      />
      <div
        ref={panel}
        role="dialog"
        aria-label="Write a scenario"
        data-leaving={leaving || undefined}
        onAnimationEnd={onLeft}
        className={`overlay-panel relative max-h-full w-full max-w-sm overflow-y-auto p-5 ${solidSurface}`}
      >
        <div className="flex items-center justify-between gap-4">
          <p className="text-base font-medium">Write a scenario</p>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className={`relative flex size-8 shrink-0 items-center justify-center rounded-full bg-foreground/10 text-foreground hover:bg-foreground/15 ${panelMotion} ${press} ${focusHaloFlush}`}
          >
            <X className="size-4" />
            <HapticSurface />
          </button>
        </div>

        {!account ? (
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            A scenario has to belong to somebody. Connect an account to write one.
          </p>
        ) : (
          <>
            <form
              className="mt-3 space-y-2"
              onSubmit={event => {
                event.preventDefault();
                setSubmitted(true);
                if (!VersionNumber.safeParse(versionNumber).success) return;
                void run(async () => {
                  const created = await api.presets.create({
                    body: {
                      slug: chosenSlug,
                      title: title.trim(),
                      from: null,
                      versionNumber: versionNumber.trim(),
                    },
                  });
                  await refreshPresets(queryClient);
                  await navigate({
                    to: "/presets/$slug/edit",
                    params: { slug: created.preset.slug },
                    search: { draft: created.draftId },
                  });
                });
              }}
            >
              <input
                value={title}
                onChange={event => setTitle(event.target.value)}
                maxLength={PRESET_TITLE_MAX_CHARACTERS}
                placeholder="What it's called"
                aria-label="Title"
                className="h-10 w-full rounded-xl bg-foreground/5 px-3 text-sm outline-none focus:bg-foreground/[0.07]"
              />
              <label className="flex h-8 items-center gap-1 rounded-lg px-1 text-xs text-muted-foreground">
                pax.co/presets/
                <input
                  value={chosenSlug}
                  onChange={event => setSlug(event.target.value.toLowerCase())}
                  aria-label="Its address"
                  className="min-w-0 flex-1 rounded-md bg-transparent px-1 py-0.5 text-foreground outline-none focus:bg-foreground/5"
                />
              </label>
              <label className="flex h-10 items-center gap-2 rounded-xl bg-foreground/5 px-3 text-sm focus-within:bg-foreground/[0.07]">
                <span className="shrink-0 text-muted-foreground">Version number</span>
                <span className="ml-1 text-muted-foreground">v</span>
                <input
                  {...numberLimit.field}
                  value={versionNumber}
                  onChange={event => {
                    setVersionNumber(event.target.value);
                    setSubmitted(false);
                  }}
                  inputMode="decimal"
                  placeholder="1"
                  className="-ml-1.5 min-w-0 flex-1 bg-transparent tabular-nums outline-none placeholder:text-muted-foreground/50"
                />
              </label>
              {numberProblem && <p className="px-1 text-xs text-destructive">{numberProblem}</p>}
              <button
                type="submit"
                disabled={busy || !title.trim() || !SLUG.test(chosenSlug)}
                className={`h-9 w-full rounded-full bg-foreground text-sm font-medium text-background hover:bg-foreground/85 disabled:opacity-40 ${panelMotion} ${press} ${focusHaloFlush}`}
              >
                {busy ? "Starting…" : "Start it blank"}
              </button>
              <p className="text-xs leading-5 text-muted-foreground">
                Or copy a scenario from its page, to begin from its world.
              </p>
              {problem && <p className="text-xs text-destructive">{problem}</p>}
            </form>

            {(mine?.invitations.length ?? 0) > 0 && (
              <div className="mt-5">
                <p className="text-xs text-muted-foreground">Invitations</p>
                {mine?.invitations.map(invitation => (
                  <div key={invitation.preset.id} className="mt-1 flex items-center gap-2 text-sm">
                    <span className="min-w-0 flex-1">
                      <span className="block truncate font-medium">{invitation.preset.title}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {invitation.invitedBy
                          ? `From ${invitation.invitedBy.displayName}`
                          : "Waiting"}
                        {invitation.roles.length ? ` · ${invitation.roles.join(", ")}` : ""}
                      </span>
                    </span>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        run(async () => {
                          await api.presets.team.invitation.delete({
                            params: { slug: invitation.preset.slug },
                          });
                          await refreshPresets(queryClient);
                        })
                      }
                      className="h-8 rounded-full px-3 text-xs text-muted-foreground hover:bg-foreground/5"
                    >
                      Decline
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() =>
                        run(async () => {
                          await api.presets.team.invitation.accept({
                            params: { slug: invitation.preset.slug },
                          });
                          await refreshPresets(queryClient);
                          await navigate({
                            to: "/presets/$slug/edit",
                            params: { slug: invitation.preset.slug },
                          });
                        })
                      }
                      className="h-8 rounded-full bg-foreground px-3 text-xs font-medium text-background"
                    >
                      Join
                    </button>
                  </div>
                ))}
              </div>
            )}

            {(mine?.presets.length ?? 0) > 0 && (
              <div className="mt-5">
                <p className="text-xs text-muted-foreground">Yours to work on</p>
                {mine?.presets.map(preset => (
                  <Link
                    key={preset.id}
                    to="/presets/$slug/edit"
                    params={{ slug: preset.slug }}
                    className={`-mx-2 mt-0.5 flex items-center gap-2 rounded-lg px-2 py-1.5 text-sm hover:bg-foreground/5 ${panelMotion}`}
                  >
                    <span className="min-w-0 flex-1 truncate font-medium">{preset.title}</span>
                    <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
                      {preset.liveVersion ? `v${preset.liveVersion}` : "Unpublished"}
                      {preset.draftCount
                        ? ` · ${preset.draftCount} ${preset.draftCount === 1 ? "draft" : "drafts"}`
                        : ""}
                    </span>
                  </Link>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </ScreenOverlay>
  );
}
